import { useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";

import { useTypedQuery } from "hooks";
import { LeadsQueryKeys } from "modules/LeadsDashboard/queryKeys";
import { ApiInstance } from "utils/Api";

import type { DropdownItemProps } from "components/Dropdown";
import type { Checklist } from "models";

type ChecklistProps = {
  dealer_ids: number[] | null;
  location_ids: number[] | null;
};

interface InterventionQuestionType extends DropdownItemProps<string> {
  filterValue: number | null;
  groupTitle?: string;
}

export const useChecklistsQuestionsQuery = ({ dealer_ids, location_ids }: ChecklistProps) => {
  const { t } = useTranslation();

  const query = useTypedQuery({
    queryKey: LeadsQueryKeys.checklists,
    queryFn: async ({ baseUrl, endpoint }) => (await ApiInstance.post(endpoint, { dealer_ids, location_ids, embed: "question" }, baseUrl)).data.checklists,
    enabled: false,
    select: useCallback(
      (checklists: Checklist[]) => {
        const selectAllOption = { key: "all", value: "all", text: t("v8_all", "All"), filterValue: null };

        const questions = checklists.reduce<InterventionQuestionType[]>((acc, checklist) => {
          if (!checklist.active || !checklist.questions?.length) return acc;

          const key = `${checklist.id}-group-title`;
          acc.push({ key, value: key, text: checklist.name || "", filterValue: null, groupTitle: checklist.name });

          checklist.questions.forEach(question => {
            const key = `${checklist.id}-${question.id}`;
            if (!acc.some(el => el.key === key)) {
              acc.push({ key, value: key, text: question.title || "", filterValue: question.id });
            }
          });
          return acc;
        }, []);

        return questions.length > 0 ? [selectAllOption, ...questions] : [];
      },
      [t]
    )
  });

  useEffect(() => {
    const isEnabled = (location_ids && location_ids.length > 0) || (dealer_ids && dealer_ids.length > 0);

    if (isEnabled) query.refetch();
  }, [dealer_ids, location_ids]);

  return query;
};
